import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EventClient } from '@aicores/events';
import type { Module, PortalStartHereCopy, StartHereStep, StepCompleteWhen } from './types';
import { IconArrowRight, IconCheck } from './icons';

interface Props {
  copy: PortalStartHereCopy;
  events: EventClient;
  productId: string;
  copiedPromptIds: Set<string>;
  viewedBonusIds: Set<string>;
  completedAssessmentIds: Set<string>;
  modules: Module[] | undefined;
}

const MANUAL_STATE_KEY = (productId: string) => `aicores:${productId}:start_here_manual`;

function loadManualState(productId: string): Record<number, boolean> {
  try {
    const raw = window.localStorage.getItem(MANUAL_STATE_KEY(productId));
    return raw ? (JSON.parse(raw) as Record<number, boolean>) : {};
  } catch {
    return {};
  }
}

function saveManualState(productId: string, state: Record<number, boolean>) {
  try {
    window.localStorage.setItem(MANUAL_STATE_KEY(productId), JSON.stringify(state));
  } catch {
    // Private browsing mode or storage quota — silently skip persistence.
  }
}

function isAutoComplete(
  condition: StepCompleteWhen,
  copiedPromptIds: Set<string>,
  viewedBonusIds: Set<string>,
  completedAssessmentIds: Set<string>,
  modules: Module[] | undefined,
): boolean {
  switch (condition.type) {
    case 'prompt_copied':
      return copiedPromptIds.has(condition.prompt_id);
    case 'module_complete': {
      const mod = modules?.find((m) => m.module_id === condition.module_id);
      if (!mod) return false;
      return mod.prompt_ids.every((id) => copiedPromptIds.has(id));
    }
    case 'bonus_viewed':
      return viewedBonusIds.has(condition.bonus_id);
    case 'assessment_completed':
      return completedAssessmentIds.has(condition.bonus_id);
    case 'manual':
      return false;
  }
}

export function StartHereBlock({
  copy,
  events,
  productId,
  copiedPromptIds,
  viewedBonusIds,
  completedAssessmentIds,
  modules,
}: Props) {
  const mountedAt = useRef<number>(Date.now());
  const fired = useRef(false);
  const [manualState, setManualState] = useState<Record<number, boolean>>(() =>
    typeof window !== 'undefined' ? loadManualState(productId) : {},
  );

  useEffect(() => {
    const start = mountedAt.current;
    const fire = () => {
      if (fired.current) return;
      fired.current = true;
      events.fire('start_here_viewed', { visible_duration_ms: Date.now() - start });
    };
    const timer = window.setTimeout(fire, 1500);
    return () => {
      window.clearTimeout(timer);
      fire();
    };
  }, [events]);

  const toggleManual = useCallback(
    (index: number) => {
      setManualState((prev) => {
        const next = { ...prev, [index]: !prev[index] };
        saveManualState(productId, next);
        return next;
      });
    },
    [productId],
  );

  const activateTarget = useCallback((step: StartHereStep) => {
    if (!step.target) return;
    const el = document.getElementById(`${step.target.type}-${step.target.id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Give the scroll a moment, then trigger expansion.
    window.setTimeout(() => {
      if (step.target?.type === 'prompt') {
        const header = el.querySelector<HTMLButtonElement>('.prompt-header');
        const alreadyOpen = el.classList.contains('is-expanded');
        if (!alreadyOpen) header?.click();
      } else if (step.target?.type === 'bonus') {
        const trigger = el.querySelector<HTMLButtonElement>('.bonus-card-trigger');
        if (!trigger) return;
        // Feature bonuses (Priority Finder) always open their modal on click.
        // Prompt bonuses toggle — only click if not already expanded.
        if (el.classList.contains('is-feature') || !el.classList.contains('is-expanded')) {
          trigger.click();
        }
      }
    }, 350);
  }, []);

  const stepStates = useMemo(
    () =>
      copy.steps.map((step, index) => {
        if (!step.complete_when) {
          return { complete: false, trackable: false, manual: false };
        }
        if (step.complete_when.type === 'manual') {
          return {
            complete: manualState[index] === true,
            trackable: true,
            manual: true,
          };
        }
        return {
          complete: isAutoComplete(
            step.complete_when,
            copiedPromptIds,
            viewedBonusIds,
            completedAssessmentIds,
            modules,
          ),
          trackable: true,
          manual: false,
        };
      }),
    [copy.steps, manualState, copiedPromptIds, viewedBonusIds, completedAssessmentIds, modules],
  );

  return (
    <section className="start-here">
      <div className="start-here-label">{copy.label}</div>
      <h2 className="start-here-title">{copy.title}</h2>
      <div className="start-here-steps">
        {copy.steps.map((step, i) => {
          const state = stepStates[i]!;
          const classes = ['step'];
          if (state.complete) classes.push('is-complete');
          if (state.manual) classes.push('is-manual');
          if (step.target) classes.push('has-target');

          return (
            <div className={classes.join(' ')} key={step.heading}>
              <div className="step-number">
                {state.manual ? (
                  <button
                    type="button"
                    className="step-checkbox"
                    aria-label={
                      state.complete ? 'Mark step as not done' : 'Mark step as done'
                    }
                    aria-pressed={state.complete}
                    onClick={() => toggleManual(i)}
                  >
                    {state.complete ? <IconCheck /> : null}
                  </button>
                ) : state.complete ? (
                  <span className="step-check" aria-label="Complete">
                    <IconCheck />
                  </span>
                ) : (
                  <span>{String(i + 1).padStart(2, '0')}</span>
                )}
              </div>
              <div className="step-content">
                <strong>{step.heading}</strong>
                {step.body}
                {step.target ? (
                  <button
                    type="button"
                    className="step-cta"
                    onClick={() => activateTarget(step)}
                  >
                    Take me there
                    <IconArrowRight aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
